import { definePreset, updatePrimaryPalette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import type { PaletteDesignToken } from '@primeuix/themes/types';

export const brandColors = {
  primary: {
    50: '#EBF6FF',
    100: '#CCE7FF',
    200: '#ADDBFF',
    300: '#4FAFFD',
    400: '#298DF4',
    500: '#1570EF',
    600: '#175ACF',
    700: '#194BAE',
    800: '#183F81',
    900: '#0F2852',
  },
};

export const semanticColors = {
  success: {
    50: '#E8FDF0',
    100: '#07462D',
    200: '#A4EEC2',
    300: '#72DFA5',
    400: '#16AB66',
    500: '#078D51',
    600: '#067043',
    700: '#085938',
    800: '#07462D',
    900: '#052E1E',
  },
  warning: {
    50: '#FFF4E5',
    100: '#FEEEC3',
    200: '#FEDE86',
    300: '#FEC748',
    400: '#F28D08',
    500: '#D86603',
    600: '#B04507',
    700: '#8D350C',
    800: '#732C0C',
    900: '#491B08',
  },
  error: {
    50: '#FEF3F2',
    100: '#FEDFDC',
    200: '#FEC7C3',
    300: '#FD9C96',
    400: '#F04033',
    500: '#D52C20',
    600: '#BC251A',
    700: '#8C1F17',
    800: '#762519',
    900: '#50150B',
  },
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
};

const primaryPalette: PaletteDesignToken = {
  50: brandColors.primary[50],
  100: brandColors.primary[100],
  200: brandColors.primary[200],
  300: brandColors.primary[300],
  400: brandColors.primary[400],
  500: brandColors.primary[500],
  600: brandColors.primary[600],
  700: brandColors.primary[700],
  800: brandColors.primary[800],
  900: brandColors.primary[900],
};

export const customTheme = definePreset(Aura, {
  extend: {
    typography: {
      fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      weight: { regular: '400', medium: '500', semibold: '600', bold: '700' },
      display: { fontSize: '1.875rem', lineHeight: '2.375rem' },
      heading: { fontSize: '1.5rem', lineHeight: '2rem' },
      page: { fontSize: '1.25rem', lineHeight: '1.75rem' },
      section: { fontSize: '1rem', lineHeight: '1.5rem' },
      bodyLarge: { fontSize: '1rem', lineHeight: '1.5rem' },
      body: { fontSize: '0.875rem', lineHeight: '1.25rem' },
      caption: { fontSize: '0.75rem', lineHeight: '1rem' },
      label: { fontSize: '0.75rem', lineHeight: '1.125rem' },
    },
    spacing: {
      step1: '0.5rem',
      step2: '1rem',
      step3: '1.5rem',
      step4: '2rem',
      step5: '2.5rem',
      step6: '3rem',
      step7: '3.5rem',
      step8: '4rem',
    },
    control: { height: '2.5rem', compactHeight: '2rem' },
    shape: { pill: '9999px' },
    stroke: { thin: '1px', base: '2px', thick: '3px', extra: '4px' },
    elevation: {
      sm: '0 2px 4px rgba(0, 0, 0, 0.1)',
      base: '0 4px 12px rgba(0, 0, 0, 0.08)',
      md: '0 6px 16px rgba(0, 0, 0, 0.12)',
      lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      card: '0 0 0.5px rgba(0, 0, 0, 0.12), 0 10px 16px rgba(0, 0, 0, 0.12), 0 2px 5px rgba(0, 0, 0, 0.15)',
      popoverMenu:
        '0 0 0.5px rgba(0, 0, 0, 0.15), 0 5px 12px rgba(0, 0, 0, 0.13), 0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    assetCategory: {
      color: '#475467',
      countColor: '#98a2b3',
      activeBackground: '#eff6ff',
      activeColor: '#175cd3',
      activeBorderColor: '#d1e9ff',
      borderRadius: '7px',
    },
    workspace: {
      background: '#f9fafb',
      panelBackground: '#ffffff',
      panelBorderColor: '#e5e7eb',
      panelBorderRadius: '{border.radius.lg}',
      contentWidth: '72rem',
      sidebarWidth: '20rem',
      sectionGap: '{spacing.step3}',
    },
  },
  semantic: {
    primary: primaryPalette,
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
          contrastColor: '#ffffff',
        },
      },
    },
    formField: {
      borderRadius: '{border.radius.sm}',
      paddingX: '0.75rem',
      paddingY: '0.5625rem',
      sm: { fontSize: '{typography.body.font.size}', paddingX: '0.75rem', paddingY: '0.3125rem' },
      lg: { fontSize: '{typography.body.large.font.size}', paddingX: '1rem', paddingY: '0.6875rem' },
      focusRing: {
        width: '3px',
        style: 'solid',
        color: '{primary.100}',
        offset: '1px',
        shadow: 'none',
      },
    },
    success: semanticColors.success,
    warning: semanticColors.warning,
    error: semanticColors.error,
    info: semanticColors.info,
  },
  components: {
    button: {
      root: {
        label: { fontWeight: '{typography.weight.semibold}' },
        roundedBorderRadius: '{shape.pill}',
        sm: { fontSize: '{typography.body.font.size}', paddingX: '0.75rem', paddingY: '0.3125rem' },
      },
    },
    card: {
      root: { borderRadius: '{workspace.panel.border.radius}', shadow: '{elevation.base}' },
      body: { padding: '{spacing.step3}', gap: '{spacing.step2}' },
      title: { fontSize: '{typography.section.font.size}', fontWeight: '{typography.weight.semibold}' },
    },
    dialog: {
      root: { borderRadius: '{workspace.panel.border.radius}', shadow: '{elevation.xl}' },
      header: { padding: '{spacing.step3}' },
      title: { fontSize: '{typography.page.font.size}', fontWeight: '{typography.weight.semibold}' },
      content: { padding: '0 {spacing.step3} {spacing.step3}' },
      footer: { padding: '0 {spacing.step3} {spacing.step3}', gap: '{spacing.step1}' },
    },
    drawer: {
      header: { padding: '{spacing.step3}' },
      title: { fontSize: '{typography.page.font.size}', fontWeight: '{typography.weight.semibold}' },
      content: { padding: '0 {spacing.step3} {spacing.step3}' },
    },
    tag: {
      root: { fontSize: '{typography.caption.font.size}', fontWeight: '{typography.weight.medium}' },
    },
    badge: {
      root: { fontSize: '{typography.caption.font.size}', fontWeight: '{typography.weight.semibold}' },
    },
    tabs: { tab: { fontWeight: '{typography.weight.medium}', padding: '{spacing.step2}' } },
    accordion: { header: { fontWeight: '{typography.weight.semibold}', padding: '{spacing.step2}' } },
  },
  css: ({ dt }) => `
    body {
      font-family: ${dt('typography.font.family')};
      font-size: ${dt('typography.body.font.size')};
      line-height: ${dt('typography.body.line.height')};
      font-weight: ${dt('typography.weight.regular')};
    }
    button, input, select, textarea {
      font-family: inherit;
    }
    :root :is(.p-inputtext, .p-textarea, .p-select, .p-select-label, .p-multiselect,
    .p-multiselect-label, .p-autocomplete, .p-button, .p-datepicker, .p-menu,
    .p-popover, .p-dialog, .p-drawer, .p-select-option, .p-multiselect-option) {
      font-family: ${dt('typography.font.family')};
      font-size: ${dt('typography.body.font.size')};
      line-height: ${dt('typography.body.line.height')};
    }
    :root :is(.p-inputtext-lg, .p-textarea-lg, .p-button-lg),
    :root :is(.p-select-lg, .p-multiselect-lg) :is(.p-select-label, .p-multiselect-label) {
      font-size: ${dt('typography.body.large.font.size')};
      line-height: ${dt('typography.body.large.line.height')};
    }
  `,
  primitive: {
    borderRadius: {
      none: '0',
      xs: '0.25rem',
      sm: '0.5rem',
      md: '0.75rem',
      lg: '1rem',
      xl: '1.5rem',
    },
  },
});

export const themeVariants = {
  light: customTheme,
};

export const defaultTheme = customTheme;
